import React, { useState, useMemo } from "react";
import { Calculator, Sparkles, TrendingDown, ShieldCheck } from "lucide-react";
import StaticPage from "../../components/StaticPage";

const SavingsCalculator = () => {
    const [monthly, setMonthly] = useState("3000");
    const [hasChronic, setHasChronic] = useState(true);

    const result = useMemo(() => {
        const m = Math.max(0, parseFloat(monthly) || 0);
        // Cumulative savings model: 8% catalog discount + 10% subscription + 5% Elite
        const catalogSave = m * 0.08;
        const subSave = hasChronic ? m * 0.10 : 0;
        const eliteSave = m * 0.05;
        const monthlyTotal = catalogSave + subSave + eliteSave;
        return {
            monthly: monthlyTotal,
            yearly: monthlyTotal * 12 - 499, // minus Elite annual fee
            percent: m > 0 ? Math.round((monthlyTotal / m) * 100) : 0,
        };
    }, [monthly, hasChronic]);

    return (
        <StaticPage
            title="Savings Calculator"
            crumb="Savings Calculator"
            testId="page-savings"
            intro="Estimate how much you could save on monthly medicine spend with PILLS' catalog discounts, subscriptions and Elite membership combined."
        >
            <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
                <div className="rounded-2xl border border-zinc-100 bg-white p-6 dark:border-white/10 dark:bg-white/5">
                    <div className="flex items-center gap-2">
                        <Calculator className="h-5 w-5 text-brand-700" />
                        <h2 className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">Your numbers</h2>
                    </div>
                    <label className="mt-5 block">
                        <span className="label-uppercase">Monthly medicine spend (₹)</span>
                        <input
                            type="number"
                            min="0"
                            step="100"
                            value={monthly}
                            onChange={(e) => setMonthly(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none ring-brand-200 focus:ring-2 dark:border-white/10 dark:bg-zinc-900"
                            data-testid="savings-monthly-input"
                        />
                    </label>
                    <label className="mt-4 flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                        <input
                            type="checkbox"
                            checked={hasChronic}
                            onChange={(e) => setHasChronic(e.target.checked)}
                            className="h-4 w-4 accent-brand-700"
                            data-testid="savings-chronic-toggle"
                        />
                        I take meds for a chronic condition (diabetes, hypertension, thyroid, etc.)
                    </label>
                </div>

                <div className="rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-purple-50 p-6 dark:border-brand-700/40 dark:from-brand-900/30 dark:to-purple-900/20" data-testid="savings-result">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-brand-700" />
                        <h2 className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-100">You could save</h2>
                    </div>
                    <p className="mt-3 text-4xl font-bold tracking-tight text-brand-700 dark:text-brand-300" data-testid="savings-yearly">
                        ₹{Math.round(Math.max(0, result.yearly)).toLocaleString("en-IN")}<span className="text-base font-medium text-zinc-500"> / year</span>
                    </p>
                    <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
                        That's <strong>~₹{Math.round(result.monthly).toLocaleString("en-IN")}/month</strong> or about <strong>{result.percent}%</strong> off your current spend (after Elite ₹499 annual fee).
                    </p>
                    <ul className="mt-5 space-y-2 text-xs text-zinc-600 dark:text-zinc-400">
                        <li className="flex items-center gap-2"><TrendingDown className="h-3 w-3 text-emerald-600" /> 8% average catalog discount vs. MRP</li>
                        <li className="flex items-center gap-2"><TrendingDown className="h-3 w-3 text-emerald-600" /> +10% subscription discount for chronic meds</li>
                        <li className="flex items-center gap-2"><ShieldCheck className="h-3 w-3 text-brand-700" /> +5% Elite member discount (₹499/yr)</li>
                    </ul>
                </div>
            </div>
            <p className="mt-6 text-xs text-zinc-400">Estimate is illustrative. Actual savings vary by SKU, supplier and applicable promotions.</p>
        </StaticPage>
    );
};

export default SavingsCalculator;
